import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
} from '@/components/ui/sheet';
import { useToast } from '@/hooks/use-toast';
import {
  Save,
  Loader2,
  TrendingUp,
  PiggyBank,
  ArrowRight,
  HelpCircle,
  Info,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Handshake,
  Wallet,
  SplitSquareVertical,
  Shield,
  ShieldCheck,
  Building2,
  X,
  Calculator,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  type PricingInputs,
  type PricingOutputs,
  computePricing,
  dbRowsToInputs,
  inputsToDbRows,
  getDefaultPricingInputs,
} from '@/lib/pricing';
import {
  type ValuationCategoryRecord,
  type ValuationItem,
  computeTotalCost,
  computeValuationInsurance,
  computeCashPrice,
  formatGHS,
} from '@/lib/valuation';

// ─── Display helper — shows 2dp, hover reveals full precision + copy ───

function CopyableValue({ raw, children }: { raw: string; children: React.ReactNode }) {
  const [copied, setCopied] = useState(false);
  const copy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(raw);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };
  return (
    <span className="group/val inline-flex items-center gap-1 relative" title={raw}>
      {children}
      <button
        onClick={copy}
        className="opacity-0 group-hover/val:opacity-100 transition-opacity duration-150 text-muted-foreground hover:text-foreground p-0.5 -mr-1"
        aria-label="Copy full value"
      >
        {copied
          ? <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
          : <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
        }
      </button>
    </span>
  );
}

/** Render a currency value: 2dp display, full precision on hover + copy */
function GHS({ value }: { value: number }) {
  return <CopyableValue raw={String(value)}>{formatGHS(value)}</CopyableValue>;
}

/** Render a numeric value: custom dp display, full precision on hover + copy */
function Num({ value, dp = 2, suffix = '' }: { value: number; dp?: number; suffix?: string }) {
  return <CopyableValue raw={String(value)}>{value.toFixed(dp)}{suffix}</CopyableValue>;
}

/** Format weeks to (Xyr Ymo) string */
function formatDuration(weeks: number) {
  if (isNaN(weeks) || weeks <= 0) return '';
  const yearsFloat = weeks / 52;
  const y = Math.floor(yearsFloat);
  const m = Math.round((yearsFloat % 1) * 12);
  if (m === 12) return `(${y + 1}yr 0mo)`;
  if (y === 0) return `(${m}mo)`;
  return `(${y}yr ${m}mo)`;
}

// ─── Help Sheet (Step-based) ───────────────────────────

const HELP_STEPS = [
  { id: 'deal', label: 'The Deal', icon: Handshake, description: 'Deal structure and pricing' },
  { id: 'investments', label: 'Investments', icon: Wallet, description: 'Capital contributions' },
  { id: 'weekly', label: 'Weekly Split', icon: SplitSquareVertical, description: 'Payment breakdown' },
  { id: 'ins-y1', label: 'Insurance Yr 1', icon: Shield, description: 'Year 1 insurance details' },
  { id: 'ins-y2', label: 'Insurance Yr 2+', icon: ShieldCheck, description: 'Renewal scenarios' },
  { id: 'partner', label: 'Partner Returns', icon: TrendingUp, description: 'ROI and returns summary' },
  { id: 'primelane', label: 'Primelane', icon: Building2, description: 'Revenue streams' },
] as const;

function HelpTable({ headers, rows }: { headers: string[]; rows: (string | number)[][] }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border/60">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted/50">
            {headers.map((h, i) => (
              <th key={h} className={cn('px-3 py-2.5 font-medium text-muted-foreground whitespace-nowrap text-xs', i > 0 ? 'text-right' : 'text-left')}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-t border-border/30">
              {row.map((cell, j) => (
                <td key={j} className={cn('px-3 py-2 whitespace-nowrap text-sm', j > 0 && 'text-right tabular-nums font-medium')}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HelpNote({ type = 'info', children }: { type?: 'info' | 'warning'; children: React.ReactNode }) {
  return (
    <div className={cn(
      'flex gap-2.5 rounded-lg px-3.5 py-2.5 text-sm leading-relaxed',
      type === 'info' && 'bg-blue-500/10 text-blue-700 dark:text-blue-400',
      type === 'warning' && 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
    )}>
      {type === 'info' ? <Info className="h-4 w-4 mt-0.5 shrink-0" /> : <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />}
      <div>{children}</div>
    </div>
  );
}

function PricingHelpSheet({
  open,
  onOpenChange,
  inputs,
  outputs,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inputs: PricingInputs;
  outputs: PricingOutputs;
}) {
  const [step, setStep] = useState(0);
  const weeklyTotal = outputs.partnerWeekly + outputs.managementWeekly + outputs.insuranceWeekly;
  const poolWeekly = outputs.poolWeekly;
  const cashPrice = inputs.partnerInvestment;
  const ratePct = (inputs.annualInterestRate * 100).toFixed(1);
  const mgtPct = (inputs.managementFeeRate * 100).toFixed(1);
  const partnerPct = ((1 - inputs.managementFeeRate) * 100).toFixed(1);
  const managementFee = outputs.partnerGrossProfit - outputs.partnerNetProfit;

  useEffect(() => { if (open) setStep(0); }, [open]);

  const currentStep = HELP_STEPS[step];
  const isFirst = step === 0;
  const isLast = step === HELP_STEPS.length - 1;

  const renderStepContent = () => {
    switch (currentStep.id) {
      case 'deal':
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              The <strong>Cash price</strong> is what the partner puts in ({formatGHS(cashPrice)}): the
              price at which the vehicle could be bought outright for cash, the purchase price plus
              preparation costs such as registration and accessories. It does not include insurance.
              Primelane finances the Year 1 insurance separately
              ({formatGHS(inputs.insuranceInvestment)}).
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              All investments earn the same <strong>{ratePct}% annual interest rate</strong>. The
              contract duration is derived from this rate: it ends when the partner return and the
              management fee are met. The Year 1 insurance is settled on its own 52-week track.
            </p>
            <HelpTable
              headers={['', 'Value']}
              rows={[
                ['Cash price', formatGHS(cashPrice)],
                ['Insurance financed by Primelane (Year 1)', formatGHS(inputs.insuranceInvestment)],
                ['Annual interest rate', `${ratePct}%`],
                ['Weekly repayment (Year 1)', formatGHS(inputs.weeklyRate)],
                ['Contract duration (derived)', `${outputs.contractWeeks.toFixed(2)} weeks ${formatDuration(outputs.contractWeeks)}`],
                ['Driver price (derived)', formatGHS(outputs.driverPrice)],
              ]}
            />
            <HelpNote>
              The driver price ({formatGHS(outputs.driverPrice)}) is <em>derived</em> from the interest rate,
              not set manually. It is the partner + management pool (pool weekly × contract weeks) plus
              the Year 1 insurance total ({formatGHS(outputs.insuranceTotal)}). Changing the interest
              rate changes the contract duration and the driver price.
            </HelpNote>
          </div>
        );

      case 'investments':
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              The <strong>partner</strong> puts in the Cash price of {formatGHS(cashPrice)}, covering the vehicle
              purchase and operational prep costs. <strong>Primelane</strong> separately finances the Year 1
              insurance ({formatGHS(inputs.insuranceInvestment)}) on behalf of the driver.
              Both earn the same {ratePct}% annual return.
            </p>
            <HelpTable
              headers={['Party', 'Investment', 'Annual Rate', 'Notes']}
              rows={[
                ['Partner', formatGHS(inputs.partnerInvestment), `${ratePct}%`, 'Vehicle & operational costs'],
                ['Primelane (insurance)', formatGHS(inputs.insuranceInvestment), `${ratePct}%`, `Actual cost: ${formatGHS(inputs.insuranceActualCost)}`],
                ['Primelane (management)', '—', '—', 'No capital, fee-based'],
              ]}
            />
            <HelpNote>
              Insurance is settled in <strong>Year 1 only</strong> (52 weeks). The partner investment
              is repaid over the full contract duration ({outputs.contractWeeks.toFixed(2)} weeks).
            </HelpNote>
          </div>
        );

      case 'weekly':
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              In Year 1 the driver pays a flat {formatGHS(inputs.weeklyRate)} a week, split three ways.
              The insurance slot runs for 52 weeks and settles the Year 1 insurance. From week 53 the
              driver pays the pool weekly ({formatGHS(poolWeekly)}), unless Primelane advances the
              renewal, which is added on top.
            </p>
            <HelpTable
              headers={['Component', 'Per Week', '% of Year 1 weekly', 'Constant?']}
              rows={[
                ['Partner', formatGHS(outputs.partnerWeekly), `${weeklyTotal > 0 ? ((outputs.partnerWeekly / weeklyTotal) * 100).toFixed(2) : 0}%`, '✅ Always'],
                ['Management fee', formatGHS(outputs.managementWeekly), `${weeklyTotal > 0 ? ((outputs.managementWeekly / weeklyTotal) * 100).toFixed(2) : 0}%`, '✅ Always'],
                ['Insurance', formatGHS(outputs.insuranceWeekly), `${weeklyTotal > 0 ? ((outputs.insuranceWeekly / weeklyTotal) * 100).toFixed(2) : 0}%`, 'Year 1 (52 weeks)'],
                ['Total', formatGHS(weeklyTotal), '100%', ''],
              ]}
            />
            <HelpNote>
              In Year 1 the driver pays {formatGHS(inputs.weeklyRate)}/week; from week 53, {formatGHS(poolWeekly)}/week
              plus any renewal advance Primelane makes.
            </HelpNote>
          </div>
        );

      case 'ins-y1':
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Insurance must be <strong>fully settled within Year 1</strong> (52 weeks) to mitigate
              termination risk. The insurance investment plus interest at {ratePct}% is
              recovered via the weekly insurance payment.
            </p>
            <HelpTable
              headers={['', 'Value']}
              rows={[
                ['Insurance investment', formatGHS(inputs.insuranceInvestment)],
                [`Interest at ${ratePct}%`, formatGHS(outputs.insuranceInterest)],
                ['Total to recover (Year 1)', formatGHS(outputs.insuranceTotal)],
                ['Weekly payment', formatGHS(outputs.insuranceWeekly)],
                ['Break-even', `${outputs.insuranceBreakevenWeeks} weeks ${formatDuration(outputs.insuranceBreakevenWeeks)}`],
                ['Less: Actual third-party cost', `−${formatGHS(inputs.insuranceActualCost)}`],
                ['Primelane insurance returns', formatGHS(outputs.primelaneInsuranceProfit)],
              ]}
            />
            <HelpNote>
              Insurance breaks even at week {outputs.insuranceBreakevenWeeks}. By end of Year 1 (week 52),
              insurance has recovered {formatGHS(outputs.insuranceTotal)} — the full investment plus interest.
            </HelpNote>
          </div>
        );

      case 'ins-y2':
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              From Year 2 onward, a renewal Primelane advances is a <strong>separate advance</strong>. It is
              added to the driver's total on top of the pool weekly and is not inside the driver price
              (priced on the Renewal Pricing page).
            </p>
            <h4 className="text-sm font-semibold">Operational scenario: the driver is kept at {formatGHS(inputs.weeklyRate)} after week 52 with no renewal advance</h4>
            <p className="text-xs text-muted-foreground leading-relaxed">
              This is not the base model, where the driver owes the pool weekly from week 53. If the
              driver is kept at the full {formatGHS(inputs.weeklyRate)}, the freed {formatGHS(outputs.spillPerWeek)}/wk
              is split at the management fee rate ({mgtPct}% to management, {partnerPct}% to the partner)
              and pays the contract off sooner.
            </p>
            <HelpTable
              headers={['Spill Allocation', 'Per Week']}
              rows={[
                [`Partner (${partnerPct}%)`, formatGHS(outputs.spillToPartner)],
                [`Management (${mgtPct}%)`, formatGHS(outputs.spillToManagement)],
                ['Total spill', formatGHS(outputs.spillPerWeek)],
              ]}
            />
            <HelpNote type="warning">
              The base pricing model prices one year of insurance. A renewal is priced separately
              and added to the driver's total when it is advanced.
            </HelpNote>

            <Separator className="my-2" />

            <h4 className="text-sm font-semibold">Repayment Overlap Risk</h4>
            <p className="text-xs text-muted-foreground leading-relaxed">
              The Year 1 insurance repayment is designed to settle within 52 weeks. However, if the
              driver <strong>misses one or more weekly payments</strong>, the outstanding balance carries
              forward past week 52. Since the insurance <em>cover</em> still expires at 12 months regardless
              of payment status, a Year 2 renewal is needed. If Primelane advances it, its repayment overlaps
              the Year 1 shortfall, so the driver owes <strong>both</strong> at once on top of the pool weekly.
            </p>
            <HelpTable
              headers={['Scenario', 'Weeks Missed', 'Overlap Duration']}
              rows={[
                ['No missed payments', '0', 'None — clean transition at week 53'],
                ['1 week missed', '1', '~1 week overlap'],
                ['4 weeks missed', '4', '~4 weeks overlap'],
              ]}
            />
            <HelpNote type="warning">
              During overlap the driver's weekly obligation is the pool weekly ({formatGHS(poolWeekly)}) plus the
              Year 1 shortfall plus the Year 2 renewal repayment, which can exceed {formatGHS(inputs.weeklyRate)}.
              Consistent weekly payments are critical to avoid this scenario.
            </HelpNote>
          </div>
        );

      case 'partner':
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              The partner invests {formatGHS(inputs.partnerInvestment)} and earns {ratePct}%/yr
              for {outputs.contractYears.toFixed(2)} years. Primelane deducts a {mgtPct}% management
              fee from gross returns.
            </p>
            <HelpTable
              headers={['Metric', 'Value']}
              rows={[
                ['Investment', formatGHS(inputs.partnerInvestment)],
                ['Annual rate', `${ratePct}%`],
                [`Gross profit (${outputs.contractYears.toFixed(2)} yrs)`, formatGHS(outputs.partnerGrossProfit)],
                [`Management fee (${mgtPct}%)`, `−${formatGHS(managementFee)}`],
                ['Net profit', formatGHS(outputs.partnerNetProfit)],
                ['Total returns', formatGHS(outputs.partnerTotalReturn)],
                ['Annualized ROI', `${outputs.partnerAnnualizedRoi.toFixed(2)}%/yr`],
                ['Weekly income', formatGHS(outputs.partnerWeekly)],
                ['Break-even', `${outputs.partnerBreakevenWeeks.toFixed(2)} weeks ${formatDuration(outputs.partnerBreakevenWeeks)}`],
              ]}
            />
            <HelpNote>
              Verification: {formatGHS(outputs.partnerWeekly)} × {outputs.contractWeeks.toFixed(2)} weeks
              = {formatGHS(outputs.partnerWeekly * outputs.contractWeeks)} = {formatGHS(outputs.partnerTotalReturn)}.
            </HelpNote>
          </div>
        );

      case 'primelane':
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Primelane earns from two revenue streams: the <strong>management fee</strong> (a {mgtPct}% cut
              of the partner's gross returns) and the <strong>insurance spread</strong> (the difference between
              what the driver pays for insurance and the actual cost of third-party cover).
            </p>
            <HelpTable
              headers={['Stream', 'Revenue', 'Cost', 'Returns']}
              rows={[
                ['Insurance (Year 1)', formatGHS(outputs.insuranceTotal), formatGHS(inputs.insuranceActualCost), formatGHS(outputs.primelaneInsuranceProfit)],
                [`Mgmt fee (${mgtPct}%)`, formatGHS(managementFee), '—', formatGHS(managementFee)],
                ['Total', formatGHS(outputs.insuranceTotal + managementFee), formatGHS(inputs.insuranceActualCost), formatGHS(outputs.primelaneInsuranceProfit + managementFee)],
              ]}
            />
            <h4 className="text-sm font-semibold">Weekly Summary</h4>
            <HelpTable
              headers={['Stream', 'Per Week', 'Duration']}
              rows={[
                ['Mgmt fee', formatGHS(outputs.managementWeekly), `${outputs.contractWeeks.toFixed(2)} weeks`],
                ['Insurance', formatGHS(outputs.insuranceWeekly), '52 weeks (Year 1)'],
                ['Total/wk (Year 1)', formatGHS(outputs.managementWeekly + outputs.insuranceWeekly), ''],
              ]}
            />
          </div>
        );
    }
  };

  const handleClose = () => {
    setStep(0);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent
        side="right"
        className="!inset-y-4 !right-4 !h-auto !p-0 !w-[85vw] !max-w-4xl rounded-2xl border shadow-2xl bg-background overflow-hidden flex gap-0"
      >
        {/* Left Sidebar - Steps */}
        <div className="w-[240px] shrink-0 bg-muted/30 border-r border-border/50 p-6 hidden md:flex flex-col relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2 pointer-events-none" />

          <div className="mb-8 relative z-10">
            <h2 className="text-2xl font-semibold tracking-tight">Pricing Docs</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Vehicle partnership financial structure.
            </p>
          </div>

          <div className="space-y-1 relative z-10 flex-1 overflow-y-auto -m-1 p-1">
            {HELP_STEPS.map((s, i) => {
              const Icon = s.icon;
              const isActive = i === step;

              return (
                <button
                  key={s.id}
                  onClick={() => setStep(i)}
                  className={cn(
                    'w-full group flex items-center gap-3 p-3 rounded-xl transition-all duration-300 text-left',
                    isActive ? 'bg-background shadow-sm ring-1 ring-inset ring-border' : 'hover:bg-background/50',
                  )}
                >
                  <div
                    className={cn(
                      'h-10 w-10 rounded-full flex items-center justify-center shrink-0 transition-colors duration-300',
                      isActive ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
                    )}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <p className={cn(
                      'font-medium text-sm transition-colors truncate',
                      isActive ? 'text-foreground' : 'text-muted-foreground'
                    )}>
                      {s.label}
                    </p>
                    <p className="text-xs text-muted-foreground/70 hidden lg:block truncate">
                      {s.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          <Button
            variant="ghost"
            size="sm"
            className="mt-auto w-fit text-muted-foreground hover:text-foreground p-0 h-auto hover:bg-transparent relative z-10"
            onClick={handleClose}
          >
            <X className="h-4 w-4 mr-2" />
            Close
          </Button>
        </div>

        {/* Right Content */}
        <div className="flex-1 flex flex-col h-full relative">
          {/* Mobile Header */}
          <div className="md:hidden p-4 border-b flex items-center justify-between">
            <div>
              <h2 className="font-semibold">{currentStep.label}</h2>
              <p className="text-xs text-muted-foreground">Step {step + 1} of {HELP_STEPS.length}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 flex flex-col overflow-hidden px-6 pt-6 md:px-10 md:pt-10">
              <div className="flex-none mb-6">
                <h3 className="text-2xl font-bold tracking-tight">{currentStep.label}</h3>
                <p className="text-muted-foreground">{currentStep.description}</p>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto pr-3 pb-6">
                {renderStepContent()}
              </div>
          </div>

          {/* Sticky Footer */}
          <div className="px-6 py-4 md:px-10 border-t bg-background/80 backdrop-blur-md sticky bottom-0 z-20">
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={() => setStep(step - 1)}
                disabled={isFirst}
                className="w-24 rounded-full"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>

              <span className="text-xs text-muted-foreground tabular-nums">
                {step + 1} / {HELP_STEPS.length}
              </span>

              <Button
                onClick={() => setStep(step + 1)}
                disabled={isLast}
                className="w-24 rounded-full shadow-lg hover:shadow-primary/25 transition-all"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function PricingPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch pricing inputs from DB
  const { data: dbPricingRows, isLoading: pricingLoading } = useQuery({
    queryKey: ['vehicle-pricings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vehicle_pricings')
        .select('*')
        .order('sort_order');
      if (error) throw error;
      return data as Array<{ id: number; key: string; value: string; sort_order: number }>;
    },
  });

  // Fetch valuation data for auto-fill
  const { data: valuationCategories } = useQuery({
    queryKey: ['vehicle-valuation-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vehicle_valuation_categories')
        .select('*')
        .order('sort_order');
      if (error) throw error;
      return data as ValuationCategoryRecord[];
    },
  });

  const { data: valuationItems } = useQuery({
    queryKey: ['vehicle-valuations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vehicle_valuations')
        .select('*')
        .order('sort_order');
      if (error) throw error;
      return data as ValuationItem[];
    },
  });

  // Total cost from valuation (the first-year insurance included)
  const valuationTotal = useMemo(() => {
    if (!valuationCategories || !valuationItems) return 0;
    return computeTotalCost(valuationItems, valuationCategories);
  }, [valuationCategories, valuationItems]);

  // First-year insurance inside that total
  const valuationInsurance = useMemo(() => {
    if (!valuationCategories || !valuationItems) return 0;
    return computeValuationInsurance(valuationItems, valuationCategories);
  }, [valuationCategories, valuationItems]);

  // Cash price from valuation: the total less that insurance
  const valuationCashPrice = useMemo(() => {
    if (!valuationCategories || !valuationItems) return 0;
    return computeCashPrice(valuationItems, valuationCategories);
  }, [valuationCategories, valuationItems]);

  // Local state for inputs
  const [inputs, setInputs] = useState<PricingInputs>(getDefaultPricingInputs());
  const [hasChanges, setHasChanges] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // Sync DB data to local state
  useEffect(() => {
    if (dbPricingRows && dbPricingRows.length > 0) {
      setInputs(dbRowsToInputs(dbPricingRows));
      setHasChanges(false);
    }
  }, [dbPricingRows]);

  // Compute outputs live
  const outputs: PricingOutputs = useMemo(() => computePricing(inputs), [inputs]);

  // Weekly split total
  const weeklyTotal = useMemo(
    () => outputs.partnerWeekly + outputs.managementWeekly + outputs.insuranceWeekly,
    [outputs]
  );

  // Update a single input
  const updateInput = (field: keyof PricingInputs, value: number) => {
    setInputs((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  // Auto-fill from valuation
  const autoFillFromValuation = () => {
    if (valuationTotal > 0 && valuationInsurance > 0) {
      setInputs((prev) => ({
        ...prev,
        partnerInvestment: valuationCashPrice,
        insuranceInvestment: valuationInsurance,
      }));
      setHasChanges(true);
      toast({ title: 'Auto-filled', description: `Partner investment ${formatGHS(valuationCashPrice)} and insurance ${formatGHS(valuationInsurance)} taken from the valuation; its total of ${formatGHS(valuationTotal)} includes the insurance.` });
    }
  };

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      const rows = inputsToDbRows(inputs);
      for (const row of rows) {
        const { error } = await supabase
          .from('vehicle_pricings')
          .upsert(
            {
              key: row.key,
              value: row.value,
              sort_order: row.sort_order,
              updated_at: new Date().toISOString(),
            },
            { onConflict: 'key' }
          );
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-pricings'] });
      toast({ title: 'Saved', description: 'Pricing inputs saved successfully.' });
      setHasChanges(false);
    },
    onError: (error) => {
      toast({ variant: 'destructive', title: 'Error', description: error.message });
    },
  });

  if (pricingLoading) {
    return (
      <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-6 lg:grid-cols-2">
          <Skeleton className="h-96" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  const ratePct = (inputs.annualInterestRate * 100).toFixed(1);
  const managementFee = outputs.partnerGrossProfit - outputs.partnerNetProfit;

  return (
    <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight mb-2">Vehicle Pricing</h1>
          <p className="text-muted-foreground">Financial model for partner investment, returns, and weekly splits.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setHelpOpen(true)} title="Documentation">
            <HelpCircle className="h-4 w-4" />
          </Button>
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={!hasChanges || saveMutation.isPending}
          >
            {saveMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save Inputs
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2 items-start">
        {/* Inputs Panel */}
        <div className="space-y-4 lg:sticky lg:top-20">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Pricing Inputs</CardTitle>
              <CardDescription>Adjust values to see live results</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Weekly Rate (GHS)</Label>
                <Input
                  type="number"
                  value={inputs.weeklyRate || ''}
                  onChange={(e) => updateInput('weeklyRate', parseFloat(e.target.value) || 0)}
                  className="tabular-nums"
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground font-medium">Investment Details</Label>
                {valuationTotal > 0 && (
                  <Button variant="outline" size="sm" onClick={autoFillFromValuation} className="text-xs h-7">
                    <ArrowRight className="mr-1 h-3 w-3" />
                    Pull from Valuation
                  </Button>
                )}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Partner Investment (GHS)</Label>
                  <Input
                    type="number"
                    value={inputs.partnerInvestment || ''}
                    onChange={(e) => updateInput('partnerInvestment', parseFloat(e.target.value) || 0)}
                    className="tabular-nums"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Insurance Investment (GHS)</Label>
                  <Input
                    type="number"
                    value={inputs.insuranceInvestment || ''}
                    onChange={(e) => updateInput('insuranceInvestment', parseFloat(e.target.value) || 0)}
                    className="tabular-nums"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Insurance Actual Cost (GHS)</Label>
                  <Input
                    type="number"
                    value={inputs.insuranceActualCost || ''}
                    onChange={(e) => updateInput('insuranceActualCost', parseFloat(e.target.value) || 0)}
                    className="tabular-nums"
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-muted-foreground">Annual Interest Rate</Label>
                  <span className="text-sm font-semibold tabular-nums">{(inputs.annualInterestRate * 100).toFixed(1)}%</span>
                </div>
                <Slider
                  value={[inputs.annualInterestRate * 100]}
                  onValueChange={([val]) => updateInput('annualInterestRate', Math.round(val * 10) / 1000)}
                  max={100}
                  min={10}
                  step={0.1}
                />
                <p className="text-xs text-muted-foreground">
                  Same rate for all investments. Contract: {outputs.contractWeeks > 0 ? `${outputs.contractWeeks.toFixed(2)} weeks ${formatDuration(outputs.contractWeeks)}` : '—'}.
                  Driver price: {outputs.driverPrice > 0 ? formatGHS(outputs.driverPrice) : '—'}.
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-muted-foreground">Management Fee Rate</Label>
                  <span className="text-sm font-semibold tabular-nums">{(inputs.managementFeeRate * 100).toFixed(1)}%</span>
                </div>
                <Slider
                  value={[inputs.managementFeeRate * 100]}
                  onValueChange={([val]) => updateInput('managementFeeRate', Math.round(val * 10) / 1000)}
                  max={30}
                  min={0}
                  step={0.1}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Outputs Panel */}
        <div className="space-y-4">
          {/* Weekly Split */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Weekly Split</CardTitle>
              <CardDescription>
                {weeklyTotal > 0 ? formatGHS(weeklyTotal) : '—'} / week in Year 1{outputs.contractWeeks > 52 ? <> • {formatGHS(outputs.poolWeekly)} / week after</> : null} • {outputs.contractWeeks > 0 ? <CopyableValue raw={String(outputs.contractWeeks)}>{outputs.contractWeeks.toFixed(2)} weeks {formatDuration(outputs.contractWeeks)}</CopyableValue> : '—'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Visual bar */}
              <div className="flex h-6 rounded-lg overflow-hidden mb-4">
                {weeklyTotal > 0 && (
                  <>
                    <div className="bg-blue-500" style={{ width: `${(outputs.partnerWeekly / weeklyTotal) * 100}%` }} />
                    <div className="bg-amber-500" style={{ width: `${(outputs.managementWeekly / weeklyTotal) * 100}%` }} />
                    <div className="bg-emerald-500" style={{ width: `${(outputs.insuranceWeekly / weeklyTotal) * 100}%` }} />
                  </>
                )}
              </div>
              {/* Value breakdown */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-blue-500" />
                    Partner
                  </span>
                  <span className="font-medium tabular-nums"><GHS value={outputs.partnerWeekly} /></span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />
                    Management
                  </span>
                  <span className="font-medium tabular-nums"><GHS value={outputs.managementWeekly} /></span>
                </div>
                <div className="flex items-center justify-between text-sm pt-1 border-t border-border/40">
                  <span className="font-medium">Partner + Mgmt</span>
                  <span className="font-semibold tabular-nums"><GHS value={outputs.partnerWeekly + outputs.managementWeekly} /></span>
                </div>

                <div className="flex items-center justify-between text-sm pt-2 border-t border-border/50">
                  <span className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                    Insurance
                  </span>
                  <span className="font-medium tabular-nums"><GHS value={outputs.insuranceWeekly} /></span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contract Summary */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Calculator className="h-4 w-4" />
                Contract Summary
              </CardTitle>
              <CardDescription>
                Derived from {ratePct}% annual rate
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {[
                  { label: 'Driver Price (derived)', value: <GHS value={outputs.driverPrice} />, bold: true },
                  { label: 'Contract Duration', value: <CopyableValue raw={String(outputs.contractWeeks)}>{outputs.contractWeeks.toFixed(2)} weeks {formatDuration(outputs.contractWeeks)}</CopyableValue> },
                  { label: 'Weekly Rate', value: <GHS value={inputs.weeklyRate} /> },
                  { label: 'Annual Interest Rate', value: <Num value={inputs.annualInterestRate * 100} suffix="%" /> },
                  { label: 'Insurance Total (Year 1)', value: <GHS value={outputs.insuranceTotal} />, muted: true },
                  // driverPrice = poolTotal + the Year 1 insurance total.
                  { label: 'Partner + Mgmt Pool', value: <GHS value={outputs.poolTotal} />, bold: true },
                ].map((row) => (
                  <div key={row.label} className="flex items-center justify-between py-1 border-b border-border/50 last:border-0">
                    <span className={cn('text-sm', row.muted ? 'text-muted-foreground' : '')}>{row.label}</span>
                    <span className={cn('text-sm tabular-nums', row.bold ? 'font-semibold' : '', row.muted ? 'text-muted-foreground' : '')}>{row.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Partner Returns */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Partner Returns
              </CardTitle>
              <CardDescription>
                {ratePct}%/yr × {outputs.contractYears.toFixed(1)} yrs
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {[
                  { label: 'Investment', value: <GHS value={inputs.partnerInvestment} /> },
                  { label: `Gross Profit (${ratePct}%/yr)`, value: <GHS value={outputs.partnerGrossProfit} /> },
                  { label: `Management Fee (${(inputs.managementFeeRate * 100).toFixed(1)}%)`, value: <>−<GHS value={managementFee} /></>, muted: true },
                  { label: 'Net Profit', value: <GHS value={outputs.partnerNetProfit} />, bold: true },
                  { label: 'Total Returns', value: <GHS value={outputs.partnerTotalReturn} />, bold: true },
                  { label: 'ROI (total)', value: <Num value={outputs.partnerRoiPct} suffix="%" /> },
                  { label: 'Annualized ROI', value: <Num value={outputs.partnerAnnualizedRoi} suffix="%/yr" /> },
                  { label: 'Weekly Income', value: <GHS value={outputs.partnerWeekly} /> },
                  { label: 'Break-even', value: <CopyableValue raw={outputs.partnerBreakevenWeeks.toFixed(2)}>{outputs.partnerBreakevenWeeks.toFixed(2)} weeks {formatDuration(outputs.partnerBreakevenWeeks)}</CopyableValue> },
                ].map((row) => (
                  <div key={row.label} className="flex items-center justify-between py-1 border-b border-border/50 last:border-0">
                    <span className={cn('text-sm', row.muted ? 'text-muted-foreground' : '')}>{row.label}</span>
                    <span className={cn('text-sm tabular-nums', row.bold ? 'font-semibold' : '', row.muted ? 'text-muted-foreground' : '')}>{row.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Management Returns */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Management Returns
              </CardTitle>
              <CardDescription>
                {(inputs.managementFeeRate * 100).toFixed(1)}% of partner gross profit
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {[
                  { label: 'Partner Gross Profit (basis)', value: <GHS value={outputs.partnerGrossProfit} />, muted: true },
                  { label: 'Management Fee Rate', value: <Num value={inputs.managementFeeRate * 100} suffix="%" /> },
                  { label: 'Total Management Fee', value: <GHS value={managementFee} />, bold: true },
                  { label: 'Contract Management Rate', value: (() => { const pool = outputs.partnerWeekly + outputs.managementWeekly; const rate = pool > 0 ? (outputs.managementWeekly / pool) * 100 : 0; return <CopyableValue raw={String(rate)}>{rate.toFixed(2)}%</CopyableValue>; })(), bold: true },
                ].map((row) => (
                  <div key={row.label} className="flex items-center justify-between py-1 border-b border-border/50 last:border-0">
                    <span className={cn('text-sm', row.muted ? 'text-muted-foreground' : '')}>{row.label}</span>
                    <span className={cn('text-sm tabular-nums', row.bold ? 'font-semibold' : '', row.muted ? 'text-muted-foreground' : '')}>{row.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Insurance Returns */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Insurance Returns (Year 1)
              </CardTitle>
              <CardDescription>
                Settled in 52 weeks • {ratePct}%/yr
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {[
                  { label: 'Investment', value: <GHS value={inputs.insuranceInvestment} /> },
                  { label: 'Actual Cost', value: <GHS value={inputs.insuranceActualCost} />, muted: true },
                  { label: 'Built-in Markup', value: <GHS value={inputs.insuranceInvestment - inputs.insuranceActualCost} />, muted: true },
                  { label: `Interest (${ratePct}%)`, value: <GHS value={outputs.insuranceInterest} /> },
                  { label: 'Total Return (Year 1)', value: <GHS value={outputs.insuranceTotal} />, bold: true },
                  { label: 'ROI', value: <Num value={outputs.insuranceRoiPct} suffix="%" /> },
                  { label: 'Weekly Income', value: <GHS value={outputs.insuranceWeekly} /> },
                  { label: 'Break-even', value: <CopyableValue raw={String(outputs.insuranceBreakevenWeeks)}>{outputs.insuranceBreakevenWeeks} weeks {formatDuration(outputs.insuranceBreakevenWeeks)}</CopyableValue> },
                  { label: 'Primelane Net Returns', value: <GHS value={outputs.primelaneInsuranceProfit} />, bold: true },
                ].map((row) => (
                  <div key={row.label} className="flex items-center justify-between py-1 border-b border-border/50 last:border-0">
                    <span className={cn('text-sm', row.muted ? 'text-muted-foreground' : '')}>{row.label}</span>
                    <span className={cn('text-sm tabular-nums', row.bold ? 'font-semibold' : '', row.muted ? 'text-muted-foreground' : '')}>{row.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Primelane Summary */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <PiggyBank className="h-4 w-4" />
                Primelane Returns
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {[
                  { label: 'Insurance Returns (Year 1)', value: <GHS value={outputs.primelaneInsuranceProfit} /> },
                  { label: 'Management Returns', value: <GHS value={outputs.primelaneManagementProfit} /> },
                  { label: 'Total', value: <GHS value={outputs.primelaneInsuranceProfit + outputs.primelaneManagementProfit} />, bold: true },
                ].map((row) => (
                  <div key={row.label} className="flex items-center justify-between py-1 border-b border-border/50 last:border-0">
                    <span className="text-sm">{row.label}</span>
                    <span className={cn('text-sm tabular-nums', row.bold ? 'font-semibold' : '')}>{row.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      {/* Help Sheet */}
      <PricingHelpSheet open={helpOpen} onOpenChange={setHelpOpen} inputs={inputs} outputs={outputs} />
    </div>
  );
}
